<template>
    <v-row class="ma-0 pa-0" align="center" justify="center">
      <v-col cols="12" class="chart_style ma-0 pa-0">
        <v-chart :id="chart_id" :ref="chart_ref" class="chart" :option="option" autoresize></v-chart>
      </v-col>
    </v-row>
  </template>
  
  <script>
  import VChart, { THEME_KEY } from "vue-echarts";
  import "echarts";
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
  
  use([CanvasRenderer, TitleComponent, TooltipComponent, LegendComponent]);
  export default {
    name: "HeatMap",
    props: ["chart_title", "chart_data", "chart_id", "chart_ref"],
    components: {
      VChart,
    },
    provide: {
      [THEME_KEY]: "light",
    },
    data: () => ({
      heatmap_colors_typical: [
        "#000003", "#000006", "#01010b", "#02020f", "#040415", "#060519", "#09071f", "#0c0926", "#0e0a2a", "#110c31",
        "#140d35", "#170f3c", "#1b1044", "#1e1049", "#221150", "#251155", "#2a115c", "#2d1060", "#321067", "#370f6c",
        "#3b0f6f", "#400f73", "#430f75", "#481078", "#4d117a", "#50127b", "#55137d", "#58157e", "#5d177e", "#60187f",
        "#651a80", "#691c80", "#6c1d80", "#711f81", "#742081", "#792281", "#7e2481", "#812581", "#852681", "#892881",
        "#8d2980", "#922b80", "#952c80", "#9a2d7f", "#9e2e7e", "#a3307e", "#a6317d", "#ab337c", "#b0347b", "#b3357a",
        "#b83778", "#bb3877", "#c03a75", "#c53c74", "#c83d72", "#cd3f70", "#d0416f", "#d4436d", "#d7456b", "#dc4869",
        "#e04b66", "#e24d65", "#e65162", "#e85461", "#ec585f", "#ee5d5d", "#f0605d", "#f3655c", "#f4685b", "#f66e5b",
        "#f7735c", "#f8775c", "#f97d5d", "#fa805e", "#fb8660", "#fb8a62", "#fc9064", "#fd9567", "#fd9969", "#fd9f6c",
        "#fda26f", "#fea873", "#feae76", "#feb179", "#feb77d", "#febb80", "#fec085", "#fec488", "#fec98d", "#fdcf92",
        "#fdd295", "#fdd89a", "#fddc9d", "#fde1a3", "#fce6a8", "#fceaac", "#fcf0b1", "#fcf3b5", "#fbf9bb", "#fbfcbf"
      ],
      heatmap_colors_red_blue: [
        "#0000ff", "#0200fc", "#0500f9", "#0700f7", "#0a00f4", "#0c00f2", "#0f00ef", "#1200ec", "#1400ea", "#1700e7",
        "#1900e5", "#1c00e2", "#1e00e0", "#2100dd", "#2400da", "#2600d8", "#2900d5", "#2b00d3", "#2e00d0", "#3100cd",
        "#3300cb", "#3600c8", "#3800c6", "#3b00c3", "#3e00c0", "#4000be", "#4300bb", "#4500b9", "#4800b6", "#4b00b3",
        "#4d00b1", "#5000ae", "#5200ac", "#5500a9", "#5800a6", "#5a00a4", "#5d00a1", "#5f009f", "#62009c", "#650099",
        "#670097", "#6a0094", "#6d0091", "#6f008f", "#72008c", "#74008a", "#770087", "#7a0084", "#7c0082", "#7f007f",
        "#82007c", "#84007a", "#870077", "#890075", "#8c0072", "#8f006f", "#91006d", "#94006a", "#960068", "#990065",
        "#9c0062", "#9e0060", "#a1005d", "#a4005a", "#a60058", "#a90055", "#ab0053", "#ae0050", "#b1004d", "#b3004b",
        "#b60048", "#b80046", "#bb0043", "#be0040", "#c0003e", "#c3003b", "#c50039", "#c80036", "#cb0033", "#cd0031",
        "#d0002e", "#d3002b", "#d50029", "#d80026", "#da0024", "#dd0021", "#e0001e", "#e2001c", "#e50019", "#e70017",
        "#ea0014", "#ec0012", "#ef000f", "#f2000c", "#f4000a", "#f70007", "#f90005", "#fc0002", "#ff0000"
      ]
    }),
    setup(props) {
      let xData = [];
      let yData = [];
      xData = (props.chart_data && props.chart_data['xAxis']) || [];
      yData = (props.chart_data && props.chart_data['yAxis']) || [];
      let data = (props.chart_data && props.chart_data['data']) || [];
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            const xLabel = params.name;
            const yLabel = yData[params.data[1]];
            const value = (params.data[2] * 100).toFixed(2) + '%';
            return `${params.marker}<strong>${xLabel} × ${yLabel}</strong><br/>Overlap: ${value}`;
          }
        },
        title: {
          text: props.chart_title,
          fontFamily: "Poppins",
          left: 'center',
        },
        legend: {
          show: false, // <-- hide the legend
        },
        grid: {
          left: "5%",
          right: "0%",
          top: "10%",
          bottom: "0%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: xData,
          axisLabel: {
            fontSize: 12,
            fontFamily: "Poppins",
            rotate: -70, // rotate to fit more labels
            interval: 0, // force all labels to display
          }
        },
        yAxis: {
          type: "category",
          data: yData,
          axisLabel: {
            fontSize: 12,
            fontFamily: "Poppins"
          }
        },
        visualMap: {
          show: false, // <-- hide the legend
          min: 0,
          max: 1,
          calculable: true,
          realtime: false,
          inRange: {
            color: [
              "#0000ff", "#0200fc", "#0500f9", "#0700f7", "#0a00f4", "#0c00f2", "#0f00ef", "#1200ec", "#1400ea", "#1700e7",
              "#1900e5", "#1c00e2", "#1e00e0", "#2100dd", "#2400da", "#2600d8", "#2900d5", "#2b00d3", "#2e00d0", "#3100cd",
              "#3300cb", "#3600c8", "#3800c6", "#3b00c3", "#3e00c0", "#4000be", "#4300bb", "#4500b9", "#4800b6", "#4b00b3",
              "#4d00b1", "#5000ae", "#5200ac", "#5500a9", "#5800a6", "#5a00a4", "#5d00a1", "#5f009f", "#62009c", "#650099",
              "#670097", "#6a0094", "#6d0091", "#6f008f", "#72008c", "#74008a", "#770087", "#7a0084", "#7c0082", "#7f007f",
              "#82007c", "#84007a", "#870077", "#890075", "#8c0072", "#8f006f", "#91006d", "#94006a", "#960068", "#990065",
              "#9c0062", "#9e0060", "#a1005d", "#a4005a", "#a60058", "#a90055", "#ab0053", "#ae0050", "#b1004d", "#b3004b",
              "#b60048", "#b80046", "#bb0043", "#be0040", "#c0003e", "#c3003b", "#c50039", "#c80036", "#cb0033", "#cd0031",
              "#d0002e", "#d3002b", "#d50029", "#d80026", "#da0024", "#dd0021", "#e0001e", "#e2001c", "#e50019", "#e70017",
              "#ea0014", "#ec0012", "#ef000f", "#f2000c", "#f4000a", "#f70007", "#f90005", "#fc0002", "#ff0000"
            ]
          },
        },
        series: [
          {
            name: "Overlap",
            type: "heatmap",
            data: data,
            label: {
              show: true,
              fontFamily: "Poppins",
              fontSize: 11,
              color: "#FFFFFF",
              formatter: function (params) {
                return (params.data[2] * 100).toFixed(1) + "%";
              },
            },
            emphasis: {
              itemStyle: {
                borderColor: "#333",
                borderWidth: 1,
              },
            },
            progressive: 1000,
            animation: false,
          },
        ],
      };
      return { option };
    },
    methods: {},
  };
  </script>
  
  <style scoped>
  .chart_title {
    font-family: "Poppins" !important;
    font-size: 10pt !important;
    line-height: 40px;
  
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chart_style {
    height: 60vh;
    font-family: "Poppins" !important;
  }
  </style>
  
